import { useEffect, useRef } from 'react'
import type { QuoteLine } from '../types'

interface DonutChartProps {
  lines: QuoteLine[]
  size?: number
  thickness?: number
}

export function DonutChart({ lines, size = 200, thickness = 40 }: DonutChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const cx = size / 2
    const cy = size / 2
    const radius = size / 2 - thickness / 2

    ctx.clearRect(0, 0, size, size)

    const total = lines.reduce((sum, l) => sum + l.amount, 0)

    if (total === 0 || lines.length === 0) {
      ctx.beginPath()
      ctx.arc(cx, cy, radius, 0, Math.PI * 2)
      ctx.strokeStyle = '#e5e7eb'
      ctx.lineWidth = thickness
      ctx.stroke()
      return
    }

    let startAngle = -Math.PI / 2
    const gap = 0.02

    for (const line of lines) {
      const slice = (line.amount / total) * Math.PI * 2
      if (slice < 0.01) continue

      ctx.beginPath()
      ctx.arc(cx, cy, radius, startAngle + gap, startAngle + slice - gap)
      ctx.strokeStyle = line.color
      ctx.lineWidth = thickness
      ctx.lineCap = 'butt'
      ctx.stroke()

      startAngle += slice
    }
  }, [lines, size, thickness])

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      aria-label="Service cost breakdown chart"
      role="img"
      style={{ width: size, height: size }}
    />
  )
}
